import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HEALTH_COLOR = "#8FB9B2";
const inputCls =
  "w-full px-2.5 py-1.5 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

function EditRow({ type, onSaved, onCancel }) {
  const [form, setForm] = useState({ name: type.name, description: type.description || "", icon: type.icon || "Activity" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.put(`/health/device-types/${type.id}`, form);
      toast.success("Device type updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-border/60" data-testid={`device-type-edit-${type.id}`}>
      <td className="py-2 pr-3"><input className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-type-name" /></td>
      <td className="py-2 pr-3"><input className={inputCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="edit-type-description" /></td>
      <td className="py-2 pr-3"><input className={inputCls} value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} data-testid="edit-type-icon" /></td>
      <td className="py-2 text-right whitespace-nowrap">
        <button onClick={save} disabled={busy} className="p-1.5 text-emerald-700 hover:text-emerald-900" data-testid="edit-type-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button onClick={onCancel} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid="edit-type-cancel"><X className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

export default function HealthDeviceTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", icon: "Activity" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/health/device-types");
      setTypes(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setAdding(true);
    try {
      await api.post("/health/device-types", form);
      toast.success("Device type added");
      setForm({ name: "", description: "", icon: "Activity" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/health/device-types/${id}`);
      setTypes((t) => t.filter((x) => x.id !== id));
      toast.success("Device type removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="health-device-types">
      <form onSubmit={add} className="rounded-xl border border-border bg-card p-6" data-testid="add-device-type-form">
        <h2 className="font-serif text-xl text-foreground mb-4">Add a device type</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Name</label>
            <input className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Glucose Monitor" data-testid="add-type-name" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Description</label>
            <input className={inputCls} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short description" data-testid="add-type-description" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Icon</label>
            <input className={inputCls} value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))} placeholder="lucide icon name" data-testid="add-type-icon" />
          </div>
        </div>
        <button type="submit" disabled={adding} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md font-medium text-[#2C2C2C] disabled:opacity-50" style={{ backgroundColor: HEALTH_COLOR }} data-testid="add-type-submit">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add type
        </button>
      </form>

      <div className="rounded-xl border border-border bg-card p-5 overflow-x-auto">
        <h2 className="font-serif text-xl text-foreground mb-4">Device types</h2>
        <table className="w-full text-sm" data-testid="device-types-table">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-3 font-medium">Name</th>
              <th className="py-2 pr-3 font-medium">Description</th>
              <th className="py-2 pr-3 font-medium">Icon</th>
              <th className="py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) =>
              editingId === t.id ? (
                <EditRow key={t.id} type={t} onSaved={() => { setEditingId(null); load(); }} onCancel={() => setEditingId(null)} />
              ) : (
                <tr key={t.id} className="border-b border-border/60 last:border-0" data-testid={`device-type-row-${t.id}`}>
                  <td className="py-2 pr-3 text-foreground font-medium">{t.name}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.description}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{t.icon}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditingId(t.id)} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`device-type-edit-btn-${t.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(t.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`device-type-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
